<template>
  <div class="campaign-history-modal modal-card bg-white">
    <header class="campaign-history-modal__header">
      <div>
        <h2>Historique des campagnes de relance</h2>
        <p>Campagnes envoyées sur les 30 derniers jours pour le domaine sélectionné.</p>
      </div>
      <button type="button" aria-label="Fermer" @click="$parent.close()">
        <i class="fas fa-times" />
      </button>
    </header>

    <div class="campaign-history-modal__content">
      <div v-if="campaigns.length" class="campaign-history-table-wrapper">
        <table class="campaign-history-table">
          <thead>
            <tr>
              <th>Aperçu</th>
              <th>Titre</th>
              <th>Date</th>
              <th>Destinataires</th>
              <th class="is-right">Téléchargements</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="campaign in campaigns">
              <tr :key="campaign.uuid">
                <td>
                  <button
                    type="button"
                    class="campaign-preview-button"
                    :aria-expanded="expandedCampaignUuid === campaign.uuid ? 'true' : 'false'"
                    :aria-label="expandedCampaignUuid === campaign.uuid ? 'Masquer l’aperçu' : 'Afficher l’aperçu'"
                    @click="togglePreview(campaign.uuid)"
                  >
                    <i :class="expandedCampaignUuid === campaign.uuid ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" />
                  </button>
                </td>
                <td>
                  <strong>{{ campaign.title || '-' }}</strong>
                </td>
                <td>{{ formatDate(campaign.sent_at) }}</td>
                <td>{{ formatRecipients(campaign) }}</td>
                <td class="is-right">{{ formatNumber(campaign.store_clicks) }}</td>
              </tr>
              <tr v-if="expandedCampaignUuid === campaign.uuid" :key="`${campaign.uuid}-preview`" class="campaign-preview-row">
                <td colspan="5">
                  <div class="campaign-preview-content">
                    <span>Aperçu du message</span>
                    <p>{{ campaign.body || 'Aucun aperçu disponible.' }}</p>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div v-else class="campaign-history-empty">
        <i class="far fa-paper-plane" />
        <p>Aucune campagne de relance n’a été envoyée sur cette période.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import fr from 'date-fns/locale/fr'

export default {
  name: 'UserCampaignHistoryModal',
  props: {
    campaigns: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      expandedCampaignUuid: null
    }
  },
  methods: {
    togglePreview (uuid) {
      this.expandedCampaignUuid = this.expandedCampaignUuid === uuid ? null : uuid
    },
    formatDate (value) {
      if (!value) return '-'
      try {
        return format(parseISO(value), 'dd/MM/yyyy HH:mm', { locale: fr })
      } catch (error) {
        return value
      }
    },
    formatRecipients (campaign) {
      const labels = {
        not_downloaded: 'Non téléchargés',
        verified: 'Vérifiés'
      }
      const scope = labels[campaign.scope] || 'Tous'
      return `${scope} (${this.formatNumber(campaign.recipients_count)})`
    },
    formatNumber (value) {
      return new Intl.NumberFormat('fr-FR').format(Number(value || 0))
    }
  }
}
</script>

<style scoped>
.campaign-history-modal {
  /* Buefy centre .animation-content et borne sa largeur par l'option width du
     modal.open (1120) : la carte ne doit jamais la dépasser, sinon elle déborde à droite. */
  width: 70rem;
  max-width: calc(100vw - 2rem);
  max-height: calc(100vh - 3rem);
  overflow: hidden;
  border-radius: 1rem;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.2);
}

.campaign-history-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}

.campaign-history-modal__header h2 {
  color: #111827;
  font-size: 1.25rem;
  font-weight: 700;
}

.campaign-history-modal__header p {
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.875rem;
}

.campaign-history-modal__header button {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  color: #64748b;
}

.campaign-history-modal__header button:hover {
  background: #f8fafc;
  color: #0f172a;
}

.campaign-history-modal__content {
  max-height: calc(100vh - 10rem);
  overflow: auto;
  padding: 1.25rem;
}

.campaign-history-table-wrapper {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
}

.campaign-history-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.campaign-history-table th {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #edf0f3;
  background: #f8fafc;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.campaign-history-table td {
  padding: 0.9rem 1rem;
  border-top: 1px solid #edf0f3;
  color: #334155;
  font-size: 0.875rem;
  vertical-align: middle;
}

.campaign-history-table .is-right {
  text-align: right;
}

.campaign-preview-button {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #eff6ff;
  color: #2563eb;
}

.campaign-preview-button:hover {
  background: #dbeafe;
}

.campaign-history-modal__header button:focus-visible,
.campaign-preview-button:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.campaign-preview-row td {
  padding: 0;
  background: #f8fafc;
}

.campaign-preview-content {
  padding: 1rem 1.25rem 1.25rem 4.5rem;
}

.campaign-preview-content span {
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.campaign-preview-content p {
  max-width: 54rem;
  margin-top: 0.5rem;
  color: #334155;
  font-size: 0.875rem;
  line-height: 1.6;
  white-space: pre-line;
}

.campaign-history-empty {
  display: flex;
  min-height: 15rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  border: 1px dashed #cbd5e1;
  border-radius: 0.75rem;
  color: #64748b;
  text-align: center;
}

.campaign-history-empty i {
  color: #93c5fd;
  font-size: 2rem;
}
</style>
